import { ImportProfileMapping, ImportProfileMappingResponse, useUploadProfileMapping } from "@api/profileImport";

import { ProfileLevelType } from "@api/apiClient";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import SuccessIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import { Alert, AlertTitle, Box, Button, Chip, CircularProgress, Tab, Tabs, TextField, Typography, useTheme } from "@mui/material";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useMemo, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

export interface ProfileImportDisplayProps {
  files?: File[];
  onImportComplete?: () => void;
  onCancel?: () => void;
  profileLevelType?: ProfileLevelType;
  parentId?: string;
}

const ProfileImportDisplay = observer((props: ProfileImportDisplayProps) => {
  const { files, onImportComplete, onCancel } = props;
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const containerRef = useRef(null);
  const alertRef = useRef(null);

  const [activeTab, setActiveTab] = useState(0);
  const [responseData, setResponseData] = useState<ImportProfileMappingResponse | null>(null);
  const [profileName, setProfileName] = useState<string>("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [importData, setImportData] = useState<{ data: Blob; fileName: string } | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  // Extract configuration-specific rows
  const autoMappingRows = useMemo(
    () => [...(responseData?.autoMappingResult?.successItems || []), ...(responseData?.autoMappingResult?.failureItems?.map((f) => f.importProfileMapping).filter(Boolean) || [])],
    [responseData],
  );

  const nonModelledRows = useMemo(
    () => [...(responseData?.nonModelledResult?.successItems || []), ...(responseData?.nonModelledResult?.failureItems?.map((f) => f.importProfileMapping).filter(Boolean) || [])],
    [responseData],
  );

  const rebarRows = useMemo(
    () => [
      ...(responseData?.rebarEstimationResult?.successItems || []),
      ...(responseData?.rebarEstimationResult?.failureItems?.map((f) => f.importProfileMapping).filter(Boolean) || []),
    ],
    [responseData],
  );

  const uploadProfileMapping = useUploadProfileMapping({
    onSuccess: async (response: ImportProfileMappingResponse) => {
      console.log("useUploadProfileMapping onSuccess", response);
      setResponseData(response);

      // Set profile name and editability
      if (response.profileName) {
        setProfileName(response.profileName);
      }

      // Set default active tab to first tab with data (using response data directly)
      const autoMappingCount = (response.autoMappingResult?.successItems?.length || 0) + (response.autoMappingResult?.failureItems?.length || 0);
      const nonModelledCount = (response.nonModelledResult?.successItems?.length || 0) + (response.nonModelledResult?.failureItems?.length || 0);
      const rebarCount = (response.rebarEstimationResult?.successItems?.length || 0) + (response.rebarEstimationResult?.failureItems?.length || 0);

      if (autoMappingCount > 0) setActiveTab(0);
      else if (nonModelledCount > 0) setActiveTab(1);
      else if (rebarCount > 0) setActiveTab(2);
      else setActiveTab(0); // Default to first tab even if no data

      // Show success/error messages based on results
      const hasAnyFailures = hasAnyFailureItems(response);
      const hasAnySuccesses = hasAnySuccessfulItems(response);

      if (hasAnyFailures && !hasAnySuccesses) {
        setShowErrorMessage(true);
        setShowSuccessMessage(false);
        setShowWarningMessage(false);
      } else if (hasAnySuccesses && !hasAnyFailures) {
        setShowSuccessMessage(true);
        setShowErrorMessage(false);
        setShowWarningMessage(false);
      } else if (hasAnySuccesses && hasAnyFailures) {
        setShowWarningMessage(true); // Some success, some failures
        setShowSuccessMessage(false);
        setShowErrorMessage(false);
      }
    },
  });

  // Auto-trigger file import when files are provided
  useEffect(() => {
    if (files && files.length > 0 && !responseData) {
      handleFileImport(files).catch(console.error);
    }
  }, [files, responseData]);

  // Helper functions
  const getTabCounts = (result?: { successItems?: ImportProfileMapping[]; failureItems?: any[] }) => {
    const successCount = result?.successItems?.length || 0;
    const failureCount = result?.failureItems?.length || 0;
    return { successCount, failureCount, totalCount: successCount + failureCount };
  };

  const hasAnySuccessfulItems = (response: ImportProfileMappingResponse) => {
    return (
      (response.autoMappingResult?.successItems?.length || 0) > 0 ||
      (response.nonModelledResult?.successItems?.length || 0) > 0 ||
      (response.rebarEstimationResult?.successItems?.length || 0) > 0
    );
  };

  const hasAnyFailureItems = (response: ImportProfileMappingResponse) => {
    return (
      (response.autoMappingResult?.failureItems?.length || 0) > 0 ||
      (response.nonModelledResult?.failureItems?.length || 0) > 0 ||
      (response.rebarEstimationResult?.failureItems?.length || 0) > 0 ||
      (response.validationFailures?.length || 0) > 0
    );
  };

  const hasAnyDataToProcess = (response: ImportProfileMappingResponse) => {
    // Return true if there's any actual row data (success or failure items)
    // Return false if only global validation failures exist
    return hasAnySuccessfulItems(response) || hasAnyRowLevelFailures(response);
  };

  const hasAnyRowLevelFailures = (response: ImportProfileMappingResponse) => {
    // Only count failure items (actual row data), not global validation failures
    return (
      (response.autoMappingResult?.failureItems?.length || 0) > 0 ||
      (response.nonModelledResult?.failureItems?.length || 0) > 0 ||
      (response.rebarEstimationResult?.failureItems?.length || 0) > 0
    );
  };

  // Column definitions for each configuration type
  const getAutoMappingColumns = (): GridColDef[] => [
    { field: "row", headerName: i18n.t("profile-import.grid.columns.row"), width: 80 },
    {
      field: "importStatus",
      headerName: i18n.t("profile-import.grid.columns.status"),
      width: 100,
      align: "center",
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
          {params.row.importStatus === "Error" && <ErrorIcon sx={{ color: theme.palette.error.light }} />}
          {params.row.importStatus === "Warning" && <WarningIcon sx={{ color: theme.palette.warning.light }} />}
          {params.row.importStatus === "Success" && <SuccessIcon sx={{ color: theme.palette.success.light }} />}
        </Box>
      ),
    },
    { field: "revitCategory", headerName: i18n.t("profile-import.grid.columns.revit-category"), flex: 1 },
    { field: "grsFamilyName", headerName: i18n.t("profile-import.grid.columns.grs-family-name"), flex: 1 },
    { field: "typeName", headerName: i18n.t("profile-import.grid.columns.type-name"), flex: 1 },
    { field: "system", headerName: i18n.t("profile-import.grid.columns.system"), flex: 1 },
    { field: "subSystem", headerName: i18n.t("profile-import.grid.columns.sub-system"), flex: 1 },
    { field: "systemType", headerName: i18n.t("profile-import.grid.columns.system-type"), flex: 1 },
    { field: "attribute1", headerName: i18n.t("profile-import.grid.columns.attribute-1"), flex: 1 },
    { field: "attribute2", headerName: i18n.t("profile-import.grid.columns.attribute-2"), flex: 1 },
    { field: "attribute3", headerName: i18n.t("profile-import.grid.columns.attribute-3"), flex: 1 },
    { field: "veracityId", headerName: i18n.t("profile-import.grid.columns.veracity-id"), flex: 1 },
    {
      field: "mappedVeracityMaterial",
      headerName: i18n.t("profile-import.grid.columns.mapped-material"),
      flex: 1,
      valueGetter: (_value: any, row: any) => row.mappedVeracityMaterial?.name || "",
    },
    { field: "status", headerName: i18n.t("profile-import.grid.columns.mapping-status"), width: 120 },
    {
      field: "importStatusMessage",
      headerName: i18n.t("profile-import.grid.columns.errors"),
      flex: 2,
    },
  ];

  const getNonModelledColumns = (): GridColDef[] => [
    { field: "row", headerName: i18n.t("profile-import.grid.columns.row"), width: 80 },
    {
      field: "importStatus",
      headerName: i18n.t("profile-import.grid.columns.status"),
      width: 100,
      align: "center",
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
          {params.row.importStatus === "Error" && <ErrorIcon sx={{ color: theme.palette.error.light }} />}
          {params.row.importStatus === "Warning" && <WarningIcon sx={{ color: theme.palette.warning.light }} />}
          {params.row.importStatus === "Success" && <SuccessIcon sx={{ color: theme.palette.success.light }} />}
        </Box>
      ),
    },
    { field: "revitCategory", headerName: i18n.t("profile-import.grid.columns.revit-category"), flex: 1 },
    { field: "grsFamilyName", headerName: i18n.t("profile-import.grid.columns.grs-family-name"), flex: 1 },
    { field: "typeName", headerName: i18n.t("profile-import.grid.columns.type-name"), flex: 1 },
    { field: "part", headerName: i18n.t("profile-import.grid.columns.part"), flex: 1 },
    {
      field: "percentageValue",
      headerName: i18n.t("profile-import.grid.columns.percentage-volume-additive"),
      width: 150,
      renderCell: (params: any) => (params.value ? `${params.value}` : ""),
    },
    { field: "system", headerName: i18n.t("profile-import.grid.columns.system"), flex: 1 },
    { field: "subSystem", headerName: i18n.t("profile-import.grid.columns.sub-system"), flex: 1 },
    { field: "attribute1", headerName: i18n.t("profile-import.grid.columns.attribute-1"), flex: 1 },
    { field: "attribute2", headerName: i18n.t("profile-import.grid.columns.attribute-2"), flex: 1 },
    { field: "attribute3", headerName: i18n.t("profile-import.grid.columns.attribute-3"), flex: 1 },
    { field: "veracityId", headerName: i18n.t("profile-import.grid.columns.veracity-id"), flex: 1 },
    {
      field: "mappedVeracityMaterial",
      headerName: i18n.t("profile-import.grid.columns.mapped-material"),
      flex: 1,
      valueGetter: (_value: any, row: any) => row.mappedVeracityMaterial?.name || "",
    },
    { field: "status", headerName: i18n.t("profile-import.grid.columns.mapping-status"), width: 120 },
    {
      field: "importStatusMessage",
      headerName: i18n.t("profile-import.grid.columns.errors"),
      flex: 2,
    },
  ];

  const getRebarColumns = (): GridColDef[] => [
    { field: "row", headerName: i18n.t("profile-import.grid.columns.row"), width: 80 },
    {
      field: "importStatus",
      headerName: i18n.t("profile-import.grid.columns.status"),
      width: 100,
      align: "center",
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
          {params.row.importStatus === "Error" && <ErrorIcon sx={{ color: theme.palette.error.light }} />}
          {params.row.importStatus === "Warning" && <WarningIcon sx={{ color: theme.palette.warning.light }} />}
          {params.row.importStatus === "Success" && <SuccessIcon sx={{ color: theme.palette.success.light }} />}
        </Box>
      ),
    },
    { field: "revitCategory", headerName: i18n.t("profile-import.grid.columns.revit-category"), flex: 1 },
    { field: "grsFamilyName", headerName: i18n.t("profile-import.grid.columns.grs-family-name"), flex: 1 },
    { field: "typeName", headerName: i18n.t("profile-import.grid.columns.type-name"), flex: 1 },
    { field: "part", headerName: i18n.t("profile-import.grid.columns.part"), flex: 1 },
    {
      field: "percentageValue",
      headerName: i18n.t("profile-import.grid.columns.percentage-volume-subtractive"),
      width: 150,
      renderCell: (params: any) => (params.value ? `${params.value}` : ""),
    },
    { field: "system", headerName: i18n.t("profile-import.grid.columns.system"), flex: 1 },
    { field: "subSystem", headerName: i18n.t("profile-import.grid.columns.sub-system"), flex: 1 },
    { field: "attribute1", headerName: i18n.t("profile-import.grid.columns.attribute-1"), flex: 1 },
    { field: "attribute2", headerName: i18n.t("profile-import.grid.columns.attribute-2"), flex: 1 },
    { field: "attribute3", headerName: i18n.t("profile-import.grid.columns.attribute-3"), flex: 1 },
    { field: "veracityId", headerName: i18n.t("profile-import.grid.columns.veracity-id"), flex: 1 },
    {
      field: "mappedVeracityMaterial",
      headerName: i18n.t("profile-import.grid.columns.mapped-material"),
      flex: 1,
      valueGetter: (_value: any, row: any) => row.mappedVeracityMaterial?.name || "",
    },
    { field: "status", headerName: i18n.t("profile-import.grid.columns.mapping-status"), width: 120 },
    {
      field: "importStatusMessage",
      headerName: i18n.t("profile-import.grid.columns.errors"),
      flex: 2,
    },
  ];

  const { height: containerHeight } = useResizeObserver({
    ref: containerRef,
    onResize: (size) => {
      console.log("containerRef", size);
    },
  });

  const { height: alertHeight } = useResizeObserver({
    ref: alertRef,
    onResize: (size) => {
      console.log("alertRef", size);
    },
  });

  const [gridHeight, setGridHeight] = useState(400);

  useEffect(() => {
    if (containerHeight && alertHeight) {
      setGridHeight(containerHeight - alertHeight - 16 - 80); // 80 for profile name field
    }
  }, [containerHeight, alertHeight]);

  const handleFileImport = async (files: File[]) => {
    if (!files || files.length === 0 || !files[0]) {
      return;
    }

    try {
      setIsProcessingFile(true);

      const data = await files[0].arrayBuffer();
      const blob = new Blob([new Uint8Array(data)], { type: files[0].type });
      setImportData({ data: blob, fileName: files[0].name });

      await uploadProfileMapping.mutateAsync({
        commitChanges: false,
        commitAfterWarning: false,
        profileLevelType: props.profileLevelType,
        parentId: props.parentId,
        fileParameter: { data: blob, fileName: files[0].name },
      });
    } catch (error) {
      console.error("Error processing file:", error);
      setShowErrorMessage(true);
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleConfirmChanges = async () => {
    if (!importData || !responseData) return;

    try {
      await uploadProfileMapping.mutateAsync({
        commitChanges: true, // Commit all data from all tabs
        commitAfterWarning: false,
        fileParameter: importData,
        parentId: props.parentId,
        profileLevelType: props.profileLevelType,
      });
      onImportComplete?.();
    } catch (error) {
      console.error("Error confirming changes:", error);
      setShowErrorMessage(true);
    }
  };

  const handleAbortImport = () => {
    // Clear all data and return to initial state
    setResponseData(null);
    setProfileName("");
    setImportData(null);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setShowWarningMessage(false);
    onCancel?.();
  };

  const autoMappingCounts = getTabCounts(responseData?.autoMappingResult);
  const nonModelledCounts = getTabCounts(responseData?.nonModelledResult);
  const rebarCounts = getTabCounts(responseData?.rebarEstimationResult);

  return (
    <Box ref={containerRef} sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
      {isProcessingFile ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 3,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            {i18n.t("profile-import.processing-file")}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {i18n.t("profile-import.processing-description")}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Alert Messages */}
          {showErrorMessage && (
            <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
              <Alert severity="error" sx={{ width: "100%" }}>
                <AlertTitle>Import Failed</AlertTitle>
                <Box>
                  {/* Show global validation failures first */}
                  {responseData?.validationFailures && responseData.validationFailures.length > 0 && (
                    <div>
                      {responseData.validationFailures.map((failure, index) => (
                        <div key={index}>• {failure.errorMessage}</div>
                      ))}
                    </div>
                  )}
                  {/* Show failure counts for each configuration type */}
                  {responseData?.autoMappingResult?.failureItems && responseData.autoMappingResult.failureItems.length > 0 && (
                    <div>• AutoMapping: {responseData.autoMappingResult.failureItems.length} errors</div>
                  )}
                  {responseData?.nonModelledResult?.failureItems && responseData.nonModelledResult.failureItems.length > 0 && (
                    <div>• Non-Modelled: {responseData.nonModelledResult.failureItems.length} errors</div>
                  )}
                  {responseData?.rebarEstimationResult?.failureItems && responseData.rebarEstimationResult.failureItems.length > 0 && (
                    <div>• Rebar Estimation: {responseData.rebarEstimationResult.failureItems.length} errors</div>
                  )}
                </Box>
              </Alert>
            </Box>
          )}

          {showSuccessMessage && (
            <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
              <Alert severity="success" sx={{ width: "100%" }}>
                <AlertTitle>Import Validation Successful</AlertTitle>
                All profile configurations have been validated successfully. Click "Confirm Changes" to save.
              </Alert>
            </Box>
          )}

          {showWarningMessage && (
            <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
              <Alert severity="warning" sx={{ width: "100%" }}>
                <AlertTitle>Partial Import Success</AlertTitle>
                Some items failed validation. Only successful items will be imported when you click "Confirm Changes".
              </Alert>
            </Box>
          )}

          {/* Simple dismiss button for validation-only failures */}
          {responseData && !hasAnyDataToProcess(responseData) && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button variant="outlined" onClick={handleAbortImport}>
                {i18n.t("profile-import.close")}
              </Button>
            </Box>
          )}

          {/* Profile Name Field - Only shown when import has processable data */}
          {responseData && hasAnyDataToProcess(responseData) && (
            <Box my={2}>
              <TextField
                label={i18n.t("profile-import.profile-name.label")}
                value={profileName}
                disabled
                InputProps={{ readOnly: true }}
                fullWidth
                helperText={i18n.t("profile-import.profile-name.helper-text.readonly")}
              />
            </Box>
          )}

          {/* COMMON ACTION BUTTONS - Only shown when import has processable data */}
          {responseData && hasAnyDataToProcess(responseData) && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 3, pb: 2, borderBottom: 1, borderColor: "divider" }}>
              <Button variant="outlined" onClick={handleAbortImport} disabled={uploadProfileMapping.isPending}>
                {i18n.t("profile-import.abort-changes")}
              </Button>
              <Button variant="contained" onClick={handleConfirmChanges} disabled={uploadProfileMapping.isPending || !hasAnySuccessfulItems(responseData)}>
                {i18n.t("profile-import.confirm-changes")}
              </Button>
            </Box>
          )}

          {/* Tabbed Interface - Only shown when import has processable data */}
          {responseData && hasAnyDataToProcess(responseData) && (
            <>
              <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      AutoMapping
                      {autoMappingCounts.totalCount > 0 && (
                        <Chip
                          size="small"
                          label={`${autoMappingCounts.successCount}/${autoMappingCounts.totalCount}`}
                          color={autoMappingCounts.failureCount > 0 ? "error" : "success"}
                        />
                      )}
                    </Box>
                  }
                  disabled={autoMappingCounts.totalCount === 0}
                />
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      Non-Modelled
                      {nonModelledCounts.totalCount > 0 && (
                        <Chip
                          size="small"
                          label={`${nonModelledCounts.successCount}/${nonModelledCounts.totalCount}`}
                          color={nonModelledCounts.failureCount > 0 ? "error" : "success"}
                        />
                      )}
                    </Box>
                  }
                  disabled={nonModelledCounts.totalCount === 0}
                />
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      Rebar Estimation
                      {rebarCounts.totalCount > 0 && (
                        <Chip size="small" label={`${rebarCounts.successCount}/${rebarCounts.totalCount}`} color={rebarCounts.failureCount > 0 ? "error" : "success"} />
                      )}
                    </Box>
                  }
                  disabled={rebarCounts.totalCount === 0}
                />
              </Tabs>

              {/* Tab Content - READ-ONLY GRIDS */}
              <Box sx={{ flexGrow: 1, minHeight: 0, mt: 2, height: "100%" }}>
                {/* AutoMapping Tab */}
                {activeTab === 0 && (
                  <DataGrid
                    apiRef={apiRef}
                    loading={uploadProfileMapping.isPending}
                    columns={getAutoMappingColumns()}
                    rows={autoMappingRows}
                    getRowId={(row) => `auto-${row.row}`}
                    hideFooter={false}
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={true}
                    sx={{ height: `${gridHeight}px` }}
                  />
                )}

                {/* Non-Modelled Tab */}
                {activeTab === 1 && (
                  <DataGrid
                    apiRef={apiRef}
                    loading={uploadProfileMapping.isPending}
                    columns={getNonModelledColumns()}
                    rows={nonModelledRows}
                    getRowId={(row) => `non-${row.row}`}
                    hideFooter={false}
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={true}
                    sx={{ height: `${gridHeight}px` }}
                  />
                )}

                {/* Rebar Tab */}
                {activeTab === 2 && (
                  <DataGrid
                    apiRef={apiRef}
                    loading={uploadProfileMapping.isPending}
                    columns={getRebarColumns()}
                    rows={rebarRows}
                    getRowId={(row) => `rebar-${row.row}`}
                    hideFooter={false}
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={true}
                    sx={{ height: `${gridHeight}px` }}
                  />
                )}
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
});

export default ProfileImportDisplay;
